import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Button, Col, Form, FormControl, FormGroup, Row } from "react-bootstrap";
import { Address } from "../../pages/types/Order";
import { areEqual } from "../../utils";
import LoadingButton from "../../common/LoadingButton";


type OrderAddressProps = {
    address: Address | undefined,
    handleChange: Function,
};

const labelMap: Address = {
    'name': 'Name',
    'street1': 'Street Address 1',
    'street2': 'Street Address 2',
    'city': 'City',
    'region': 'State',
    'postal_code': 'Zip',
    'country': 'Country',
    'phone': 'Phone',
};

const initialState: Address = {
    name: '',
    street1: '',
    street2: '',
    city: '',
    region: '',
    postal_code: '',
    country: 'United States',
    phone: '',
};

const requiredFields = ['name', 'street1', 'city', 'region', 'postal_code'];

const OrderAddress = ({ address, handleChange }: OrderAddressProps) => {
    const addressIsComplete = (addr: Address) => requiredFields.reduce((valid, field) => {
        if (!valid) return valid;
        return !!addr[field];
    }, true);
    const [validated, setValidated] = useState(false);
    const [editing, setEditing] = useState(false);
    const [edited, setEdited] = useState<Address | null>();
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (address) {
            if (typeof address.phone === 'undefined') {
                address.phone = '';
            }
            if (!address.country) {
                address.country = 'United States';
            }
            if (!addressIsComplete(address)) {
                setEditing(true);
            }
            setEdited({ ...address });
        } else {
            setEdited({ ...initialState });
        }
    }, [address]);

    const handleSubmit = async (e: FormEvent) => {
        const form = e.currentTarget as HTMLFormElement;
        const valid = form.checkValidity();
        e.preventDefault();
        e.stopPropagation();
        setValidated(true);
        if (valid) {
            setSaving(true);
            try {
                await handleChange(edited);
            } catch (err: any) {
                // Force a refresh of the component.
                setEdited(undefined);
                // Revert to unchanged address.
                setEdited({ ...address } as Address);
            } finally {
                setSaving(false);
                setEditing(false);
            }
        }
    };

    const handleFormChange = (e: ChangeEvent, propertyName: string) => {
        const input = e.currentTarget as HTMLInputElement;
        const changed = { ...edited, [propertyName]: input.value } as Address;
        setEdited(changed);
    }
    return (
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <h4 id="shippingAddressForm">Shipping Address</h4>
            {editing && <>
                <Row>
                    {
                        edited && Object.keys(edited).filter(p => p !== '__typename').map((p: string) => (
                            <FormGroup key={p} className="mb-3" md="6" lg="4" as={Col}>
                                <Form.Label>{labelMap[p]}</Form.Label>
                                <FormControl required={requiredFields.some(f => f === p)} onChange={(e) => handleFormChange(e, p)} aria-describedby={`order-${p}`} type="text" placeholder={labelMap[p]} value={edited[p] ? edited[p] : ''} />
                            </FormGroup>
                        ))
                    }
                </Row>
                {((!edited || !address) || (edited && address && !areEqual(edited, address))) && <LoadingButton type="submit" variant="primary" loading={saving}>{saving ? 'Saving ...' : 'Save Address'}</LoadingButton>}{' '}
                <Button variant="info" onClick={() => {
                    setEdited(address);
                    setEditing(!editing);
                }}>{(edited && address && areEqual(edited, address)) ? "Done Editing" : "Cancel"}</Button>
            </>}
            {!editing && <div>
                {edited && Object.keys(edited)
                    .filter(p => p !== '__typename')
                    .map((p: string) => {
                        if (labelMap[p] === 'Name') {
                            return <span key={labelMap[p]}><strong>{edited[p]}</strong> — </span>;
                        } else {
                            return <span key={labelMap[p]}>{edited[p] ? edited[p] + ' ' : ''}</span>;
                        }
                    })
                }
                <Button className="d-print-none ms-3" variant="outline-info" size="sm" onClick={() => setEditing(!editing)}>Edit</Button>
            </div>}
        </Form>
    );
}
export default OrderAddress;